import brandLogo from '../../assets/brand/nerds-at-play.png';
import { LiveBoard } from './LiveBoard.js';
import { useRef, useState } from 'react';
import { TeamRequestPanel } from './TeamRequestPanel.js';
import { GmRequestPanel } from './GmRequestPanel.js';
import { useBoardSession } from './useBoardSession.js';
import { TeamLoginPanel } from './TeamLoginPanel.js';
import './board.css';
import './entry.css';

type Panel = 'team' | 'gm' | 'world' | 'access';
const titles: Record<Panel, string> = {
  team: 'Enter with your team',
  gm: 'Game Master login',
  world: 'Current world',
  access: 'Board access',
};

/** Production board entry; authenticated sessions mount the connected P01 game. */
export function BoardEntry() {
  const dialog = useRef<HTMLDialogElement>(null);
  const [panel, setPanel] = useState<Panel>('team');
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    session,
    checked: sessionChecked,
    error: accessError,
    epoch,
    loggingOut,
    logout,
    refresh,
    accept,
  } = useBoardSession();
  const [joining, setJoining] = useState(false);
  const [joinName, setJoinName] = useState('');
  const open = (next: Panel) => {
    setPanel(next);
    setDialogOpen(true);
    dialog.current?.showModal();
  };
  return (
    <div className="board-preview board-entry">
      <header className="board-header">
        <div className="board-brand">
          <img src={brandLogo} alt="Nerds at Play logo" width="48" height="48" />
          <div>
            <strong>Nerds at Play</strong>
            <small>The world is what you make it.</small>
          </div>
        </div>
        <nav className="entry-navigation" aria-label="Board access">
          <button className="board-primary" onClick={() => open('team')}>
            Team entry
          </button>
          <button onClick={() => open('gm')}>
            {session?.role === 'MODERATOR' ? 'Game controls' : 'GM login'}
          </button>
          <button className="entry-access" onClick={() => open('access')}>
            Board access
          </button>
        </nav>
      </header>
      <main className="board-main">
        {accessError && !dialogOpen && (
          <p role="alert" className="entry-error">
            {accessError} <button onClick={() => void refresh()}>Check connection</button>
          </p>
        )}
        {session && (
          <p role="status" className="entry-status">
            {session.role === 'TEAM'
              ? `Signed in as ${session.entryName ?? 'team'}.`
              : 'Signed in as Game Master.'}{' '}
            <button onClick={() => open('access')}>Manage session</button>
          </p>
        )}

        <div className="board-toolbar">
          <div>
            <p className="board-kicker">The shared game board</p>
            <h1>A future, together.</h1>
          </div>
          <button
            className="board-world-link"
            onClick={() => {
              const world = document.getElementById('live-world') as HTMLDetailsElement | null;
              if (world) {
                world.open = true;
                world.scrollIntoView({ behavior: 'smooth' });
              } else open('world');
            }}
          >
            Current world <span aria-hidden="true">↗</span>
          </button>
        </div>
        {session && !loggingOut ? (
          <LiveBoard
            key={epoch}
            role={session.role}
            teamId={session.role === 'TEAM' ? session.teamId : undefined}
            onUnauthorized={() => void refresh()}
            onManageTeams={() => open('gm')}
          />
        ) : (
          <section className="board-sheet board-single-team entry-sheet" aria-label="Board entry">
            <div className="board-history">
              <div className="board-phase">
                <span>Welcome to the board</span>
              </div>
              <div className="entry-arrival">
                <section className="entry-world" aria-labelledby="world-heading">
                  <p className="board-kicker">World Broadcasts</p>
                  <h2 id="world-heading">One shared world.</h2>
                  <p>
                    Teams make their moves. The Game Master brings the consequences to the room.
                  </p>
                  <p className="entry-status">Sign in to see the game</p>
                  <p className="entry-detail">
                    The shared board opens after team or Game Master login.
                  </p>
                </section>
                <div className="board-connector" aria-hidden="true" />
                <section className="entry-team" aria-labelledby="team-heading">
                  <p className="board-kicker">Your place at the table</p>
                  <h2 id="team-heading">Play as a team.</h2>
                  <p>
                    Build an organization. Decide what future you want. Make your next move
                    together.
                  </p>
                  <button className="board-primary" onClick={() => open('team')}>
                    Team entry <span aria-hidden="true">↗</span>
                  </button>
                  <p className="entry-detail">
                    Join your approved team, or request a team and wait for Game Master approval.
                  </p>
                </section>
              </div>
            </div>
          </section>
        )}
        <p className="entry-footer">
          The game happens around the table. This board will keep its shared history.
        </p>
      </main>
      <dialog
        ref={dialog}
        className="board-dialog entry-dialog"
        aria-labelledby="entry-dialog-title"
        onClose={(event) => {
          if (event.target === event.currentTarget) setDialogOpen(false);
        }}
        onKeyDown={(event) => {
          if (
            event.key !== 'Tab' ||
            (event.target as HTMLElement).closest('dialog') !== event.currentTarget
          )
            return;
          const controls = event.currentTarget.querySelectorAll<HTMLElement>(
            'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]',
          );
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      >
        <header>
          <h2 id="entry-dialog-title">
            {panel === 'gm' && session?.role === 'MODERATOR'
              ? 'Team requests · Game controls'
              : titles[panel]}
          </h2>
          <button aria-label="Close dialog" onClick={() => dialog.current?.close()}>
            ×
          </button>
        </header>
        {dialogOpen && accessError && (
          <p role="alert" className="entry-error">
            {accessError}
          </p>
        )}
        {dialogOpen && panel === 'team' && !loggingOut && (
          <div key={epoch}>
            {session ? (
              <>
                <p>
                  {session.role === 'TEAM'
                    ? `Signed in as ${session.entryName ?? 'team'}.`
                    : 'Signed in as Game Master.'}
                </p>
                <p>To enter another team, log out of this device first.</p>
                <button onClick={() => void logout()}>Log out of this device</button>
              </>
            ) : !sessionChecked ? (
              <p>
                Checking this device’s session…{' '}
                <button onClick={() => void refresh()}>Retry session check</button>
              </p>
            ) : (
              <>
                <div className="entry-actions">
                  <button onClick={() => setJoining(true)}>Join a team</button>
                  <button onClick={() => setJoining(false)}>Requests and status</button>
                </div>
                {joining ? (
                  <TeamLoginPanel initialName={joinName} onSession={accept} />
                ) : (
                  <TeamRequestPanel
                    onJoin={(name) => {
                      setJoinName(name);
                      setJoining(true);
                    }}
                  />
                )}
              </>
            )}
          </div>
        )}
        {loggingOut && panel !== 'access' && (
          <button onClick={() => void logout()}>Retry logout</button>
        )}
        {dialogOpen && panel === 'gm' && !loggingOut && (
          <GmRequestPanel key={epoch} session={session} onSession={accept} />
        )}
        {panel === 'world' && (
          <p>
            Sign in and open Current world on the board to read the scenario and published world.
          </p>
        )}
        {panel === 'access' && (
          <>
            <p>
              {session
                ? session.role === 'MODERATOR'
                  ? 'Signed in as Game Master.'
                  : `Signed in as ${session.entryName ?? 'team'}.`
                : sessionChecked
                  ? 'No team or Game Master session is signed in on this device.'
                  : 'Your session could not be checked yet.'}
            </p>
            <p>
              Request status has separate, limited access. Use Team entry to check it. Logging out
              here also ends request-status access on this device.
            </p>
            {session && (
              <p>
                Access ends{' '}
                <time dateTime={session.expiresAt}>
                  {new Date(session.expiresAt).toLocaleString()}
                </time>
                . Ask the Game Master if you need access beyond this window.
              </p>
            )}
            <button onClick={() => void logout()}>
              {loggingOut ? 'Retry logout' : 'Log out of this device'}
            </button>
          </>
        )}
        <div className="board-dialog-actions">
          <button onClick={() => dialog.current?.close()}>Return to board</button>
        </div>
      </dialog>
    </div>
  );
}
